import { useState, useMemo, useEffect } from 'react'
import { AuditActions, type AuditLog, type Organisation } from '@esf/shared'

const renderIcon = (path: string, className = 'w-5 h-5') => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
)

interface AuditLogsPageProps {
  auditLogs: AuditLog[]
  loading: boolean
  nextKey: string | null
  actionFilter: string
  setActionFilter: (v: string) => void
  actorFilter: string
  setActorFilter: (v: string) => void
  fromDate: string
  setFromDate: (v: string) => void
  toDate: string
  setToDate: (v: string) => void
  searchQuery: string
  setSearchQuery: (v: string) => void
  orgFilter: string
  setOrgFilter: (v: string) => void
  isSuperAdmin: boolean
  organisations: Organisation[]
  fetchAuditLogs: (append?: boolean) => void
}

// Colour-coded action badges
const actionBadgeColour = (action: string): string => {
  if (action.includes('CREATE') || action.includes('RESTORE')) return 'bg-green-100 text-green-700'
  if (action.includes('UPDATE') || action.includes('COMPLETE')) return 'bg-blue-100 text-blue-700'
  if (action.includes('DELETE') || action.includes('REVOKE')) return 'bg-red-100 text-red-700'
  if (action.includes('LOGIN') || action.includes('LOGOUT')) return 'bg-yellow-100 text-yellow-700'
  return 'bg-gray-100 text-gray-700'
}

// Format action name for display
const formatAction = (action: string): string => {
  return action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// Format timestamp for display
const formatTimestamp = (ts: string): string => {
  try {
    const d = new Date(ts)
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return ts
  }
}

// Summarise details object into a short string
const summariseDetails = (details: Record<string, unknown>): string => {
  const parts: string[] = []
  for (const [key, value] of Object.entries(details)) {
    if (key === 'fields' && Array.isArray(value)) {
      parts.push(`fields: ${value.join(', ')}`)
    } else if (typeof value === 'string') {
      parts.push(`${key}: ${value}`)
    }
  }
  return parts.join(' | ') || '-'
}

export default function AuditLogsPage({
  auditLogs,
  loading,
  nextKey,
  actionFilter,
  setActionFilter,
  actorFilter,
  setActorFilter,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  searchQuery,
  setSearchQuery,
  orgFilter,
  setOrgFilter,
  isSuperAdmin,
  organisations,
  fetchAuditLogs,
}: AuditLogsPageProps) {
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  // Re-fetch when filters change (debounced via parent useCallback deps)
  useEffect(() => {
    fetchAuditLogs()
  }, [actionFilter, actorFilter, fromDate, toDate, searchQuery, orgFilter])

  const orgMap = useMemo(() => {
    const map: Record<string, string> = {}
    for (const org of organisations) {
      map[org.id] = org.name
    }
    return map
  }, [organisations])

  const hasFilters = actionFilter || actorFilter || fromDate || toDate || searchQuery || orgFilter

  const clearFilters = () => {
    setActionFilter('')
    setActorFilter('')
    setFromDate('')
    setToDate('')
    setSearchQuery('')
    setOrgFilter('')
  }

  return (
    <div className="container-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isSuperAdmin ? 'Platform-wide activity log' : 'Activity log for your organisation'}
          </p>
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => fetchAuditLogs()}
          disabled={loading}
        >
          {renderIcon('M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15')}
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
            <input
              type="text"
              placeholder="Search by actor..."
              className="input w-full"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Action</label>
            <select
              className="input"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <option value="">All Actions</option>
              {AuditActions.map(action => (
                <option key={action} value={action}>{formatAction(action)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
            <input
              type="date"
              className="input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
            <input
              type="date"
              className="input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          {isSuperAdmin && organisations.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Organisation</label>
              <select
                className="input"
                value={orgFilter}
                onChange={(e) => setOrgFilter(e.target.value)}
              >
                <option value="">All Organisations</option>
                {organisations.filter(o => o.enabled !== false).map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
          )}
          {hasFilters && (
            <button
              className="btn btn-secondary text-sm"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading && auditLogs.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <div className="animate-spin w-8 h-8 border-2 border-esf-navy border-t-transparent rounded-full mx-auto mb-3" />
            Loading audit logs...
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            {renderIcon('M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', 'w-12 h-12 mx-auto mb-3 text-gray-300')}
            <p className="text-lg font-medium">No audit logs found</p>
            <p className="text-sm mt-1">
              {hasFilters ? 'Try adjusting your filters' : 'Activity will appear here as actions are performed'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr>
                    <th className="text-left">Timestamp</th>
                    <th className="text-left">Action</th>
                    <th className="text-left">Actor</th>
                    {isSuperAdmin && <th className="text-left">Organisation</th>}
                    <th className="text-left">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log, idx) => {
                    const rowKey = `${log.ts}-${log.action}-${idx}`
                    const isExpanded = expandedRow === rowKey
                    return (
                      <>
                        <tr
                          key={rowKey}
                          className="cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => setExpandedRow(isExpanded ? null : rowKey)}
                        >
                          <td className="text-sm text-gray-600 whitespace-nowrap">
                            {formatTimestamp(log.ts)}
                          </td>
                          <td>
                            <span className={`badge text-xs font-medium ${actionBadgeColour(log.action)}`}>
                              {formatAction(log.action)}
                            </span>
                          </td>
                          <td className="text-sm text-gray-900">{log.actor}</td>
                          {isSuperAdmin && (
                            <td className="text-sm text-gray-600">
                              {orgMap[log.organisationId] || log.organisationId}
                            </td>
                          )}
                          <td className="text-sm text-gray-500 max-w-xs truncate">
                            {summariseDetails(log.details)}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr key={`${rowKey}-detail`}>
                            <td colSpan={isSuperAdmin ? 5 : 4} className="bg-gray-50 p-4">
                              <div className="text-xs font-mono whitespace-pre-wrap text-gray-700 max-h-48 overflow-y-auto">
                                {JSON.stringify(log.details, null, 2)}
                              </div>
                              <div className="mt-2 text-xs text-gray-400">
                                Organisation: {log.organisationId} | Logged at: {log.ts}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {nextKey && (
              <div className="border-t border-gray-200 p-4 text-center">
                <button
                  className="btn btn-secondary"
                  onClick={() => fetchAuditLogs(true)}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Count footer */}
      {auditLogs.length > 0 && (
        <div className="mt-3 text-sm text-gray-500 text-right">
          Showing {auditLogs.length} log{auditLogs.length !== 1 ? 's' : ''}
          {nextKey && ' (more available)'}
        </div>
      )}
    </div>
  )
}
